import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table } from 'antd'
import { TablePaginationConfig } from 'antd/es/table'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { WashingMethod } from '@/types'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: WashingMethod[] | undefined
  getWashingMethodLoading: boolean
  page: number
  pageSize: number
  totalItems: number
  onChange: (config: TablePaginationConfig) => void
  onDelete: (id: WashingMethod['id']) => void
}

const WashingMethodTable = ({
  dataSource,
  getWashingMethodLoading,
  onDelete,
  page,
  pageSize,
  totalItems,
  onChange,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const columns: ColumnsType<WashingMethod> = [
    {
      title: t('components.pages.washingMethods.list.table.no'),
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (id: number) => {
        return <CustomRowTable type="right">{id}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => {
        return <CustomRowTable type="left">{name}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.volume'),
      dataIndex: 'volume',
      key: 'volume',
      render: (time: number) => {
        return <CustomRowTable type="right">{time}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.time'),
      dataIndex: 'time',
      key: 'time',
      render: (time: number) => {
        return <CustomRowTable type="right">{time}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.cost'),
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(cost)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.water'),
      dataIndex: 'waterQuantity',
      key: 'waterQuantity',
      render: (waterQuantity: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(waterQuantity)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (record: WashingMethod) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <Button size="small" onClick={() => goToDetailWashingMethodPage(record.id)}>
                  {t('components.pages.admins.list.table.detail')}
                </Button>
                <Button size="small" onClick={() => goToEditWashingMethodPage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>{' '}
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditWashingMethodPage = (id: WashingMethod['id']) => {
    const path = routes.updateWashingMethod.path.replace(':id', id.toString())
    navigate(path)
  }

  const goToDetailWashingMethodPage = (id: WashingMethod['id']) => {
    const path = routes.washingMethodDetail.path.replace(':id', id.toString())
    navigate(path)
  }

  return (
    <Table
      rowKey="id"
      bordered
      dataSource={dataSource}
      columns={columns}
      loading={getWashingMethodLoading}
      pagination={{
        pageSize,
        current: page,
        total: totalItems,
        showSizeChanger: false,
      }}
      onChange={onChange}
      scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
    />
  )
}

WashingMethodTable.displayName = 'WashingMethodTable'
export default WashingMethodTable
